import './journey.css';
// journey.ts - feature entry (ADR-002 phase C split): loaders and event
// wiring. Render/handler modules live in journey-{state,cycles,drawer,
// dashboard,sharing}.ts; they reach the loaders through trajActions to keep
// the module graph acyclic.
import { $ } from '../shared/dom';
import { showToast } from '../shared/ui';
import { apiFetch } from '../api/client';
import { onboardingOverlay } from '../shared/onboarding-gateway';
import { D3JourneyGraph } from './D3JourneyGraph';
import { trajState, trajActions, stageConfig, isJourneyReadOnly } from './journey-state';
import {
  bindJourneySelectTooltip,
  handleGraphNodeClick,
  openOnboardingGuide,
  renderDashboard,
  renderEmptyState,
  resetViewToColumns,
  setMyceliumTabEnabled,
  setJourneyView,
  syncViewModeFromJourneyId,
  updateOnboardingGuideButton,
  updateJourneyRoleBadge,
  updateJourneySelectTooltip,
} from './journey-dashboard';
import { closeObservationDrawer, openObservationDrawer, refreshDrawerContent } from './journey-drawer';
import { initJourneySharing } from './journey-sharing';
import type { Journey } from './journey.types';

export * from './journey.types';
export { openObservationDrawer, closeObservationDrawer };

export async function loadJourneySettings() {
  try {
    const sessionId = onboardingOverlay.getSessionId();
    const settings = await apiFetch<any>(`/api/journey/settings?session_id=${encodeURIComponent(sessionId)}`);
    trajState.onboardingEnabled = settings?.onboarding?.enabled !== false;
    onboardingOverlay.configureSessionTimeout(settings?.onboarding?.session_timeout_min);
    if (settings?.onboarding) {
      onboardingOverlay.configureRuntime({
        experience:
          settings.onboarding.experience === 'video'
            ? 'video'
            : settings.onboarding.experience === 'avatar_script'
              ? 'avatar_script'
              : settings.onboarding.experience === 'avatar_live'
                ? 'avatar_live'
                : 'static',
        ab_enabled: !!settings.onboarding.ab_enabled,
        video: settings.onboarding.video,
        avatar: settings.onboarding.avatar,
      });
    }
    updateOnboardingGuideButton();
    if (settings && Array.isArray(settings.stages) && settings.stages.length > 0) {
      stageConfig.STAGE_NAMES = (settings.daily_stages || settings.stages).map((s: any) => s.name);
      stageConfig.STAGE_DESCS = (settings.daily_stages || settings.stages).map((s: any) => s.description || '');
    }
    if (settings && Array.isArray(settings.qualitative_stages) && settings.qualitative_stages.length > 0) {
      stageConfig.QUALITATIVE_STAGE_NAMES = settings.qualitative_stages.map((s: any) => s.name);
      stageConfig.QUALITATIVE_STAGE_DESCS = settings.qualitative_stages.map((s: any) => s.description || '');
    }
  } catch (err) {
    console.error('Failed to load journey settings', err);
  }
}

function normalizeJourneyList(raw: unknown): Journey[] {
  if (Array.isArray(raw)) return raw as Journey[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as { value?: unknown }).value)) {
    return (raw as { value: Journey[] }).value;
  }
  return [];
}

let loadJourneysInFlight: Promise<void> | null = null;

export async function loadJourneys() {
  if (loadJourneysInFlight) {
    return loadJourneysInFlight;
  }
  loadJourneysInFlight = loadJourneysInternal().finally(() => {
    loadJourneysInFlight = null;
  });
  return loadJourneysInFlight;
}

async function loadJourneysInternal() {
  await loadJourneySettings();
  try {
    const rawList = await apiFetch<Journey[]>('/api/journeys');
    const list = normalizeJourneyList(rawList);
    trajState.allJourneys = list;
    const select = $<HTMLSelectElement>('journey-select');
    if (!select) return;

    select.innerHTML = '';

    const my = trajState.allJourneys.filter((t) => t.role === 'owner');
    const shared = trajState.allJourneys.filter((t) => t.role !== 'owner');

    const appendGroup = (label: string, items: Journey[]) => {
      if (items.length === 0) return;
      const group = document.createElement('optgroup');
      group.label = label;
      items.forEach((t) => {
        const opt = document.createElement('option');
        opt.value = t.id;
        opt.textContent = t.name;
        if (t.description?.trim()) {
          opt.title = t.description.trim();
        }
        group.appendChild(opt);
      });
      select.appendChild(group);
    };

    appendGroup('Мои истории', my);
    appendGroup('Общие со мной', shared);

    const createBtn = $<HTMLButtonElement>('btn-create-journey');
    const hasAny = my.length > 0 || shared.length > 0;
    select.disabled = !hasAny;
    if (createBtn) {
      createBtn.disabled = false;
    }

    if (!hasAny) {
      trajState.currentJourney = null;
      setMyceliumTabEnabled(false);
      renderEmptyState();
      updateOnboardingGuideButton();
      return;
    }

    setMyceliumTabEnabled(true);

    const selectEl = $<HTMLSelectElement>('journey-select');
    if (selectEl) selectEl.disabled = false;

    const savedId = localStorage.getItem('avgexpert_last_journey_id');
    const target = trajState.allJourneys.find((x) => x.id === savedId) || my[0] || shared[0];
    if (!target?.id) {
      renderEmptyState();
      updateOnboardingGuideButton();
      return;
    }

    select.value = target.id;
    syncViewModeFromJourneyId(target.id);
    localStorage.setItem('avgexpert_last_journey_id', target.id);

    await loadJourneyDetails(target.id);
    updateOnboardingGuideButton();
  } catch (err: any) {
    console.error('Failed to load journeys', err);
    const detail = err?.message ? `: ${err.message}` : '';
    showToast(`Ошибка при загрузке историй${detail}`, 'error');
  }
}

async function loadJourneyDetails(id: string) {
  try {
    const details = await apiFetch<Journey>(`/api/journeys/${id}?depth=summary`);
    trajState.currentJourney = details;
    
    // Сбрасываем поиск
    const searchInput = $<HTMLInputElement>('journey-search-input');
    if (searchInput) searchInput.value = '';
    const searchResults = $('journey-search-results');
    if (searchResults) {
      searchResults.innerHTML = '';
      searchResults.classList.add('hidden');
    }
    
    // Update role and select tooltip
    updateJourneySelectTooltip(details);

    const role = details.role || 'reader';
    updateJourneyRoleBadge(role);

    const shareBtn = $('btn-share-journey');
    const readOnly = isJourneyReadOnly(details);
    if (shareBtn) {
      shareBtn.classList.toggle('hidden', role !== 'owner' || readOnly);
    }

    // Сбрасываем вид на Карта
    resetViewToColumns();

    renderDashboard(details);

    // Refresh active drawer if open
    if (trajState.selectedObservationId) {
      refreshDrawerContent();
    }
  } catch (err: any) {
    console.error('Failed to load journey details', err);
    showToast('Ошибка при загрузке деталей истории', 'error');
  }
}

export function initJourney() {
  bindJourneySelectTooltip();

  $('btn-onboarding-guide')?.addEventListener('click', () => openOnboardingGuide());

  // Bind Header Controls
  const createBtn = $('btn-create-journey');
  createBtn?.addEventListener('click', async () => {
    const name = prompt('Введите название новой истории развития:');
    if (!name) return;
    const description = prompt('Введите описание истории (необязательно):') || '';
    
    try {
      const created = await apiFetch<Journey>('/api/journeys', {
        method: 'POST',
        body: JSON.stringify({ name, description })
      });
      showToast('История успешно создана');
      await loadJourneys();
      // Auto select newly created journey
      const select = $<HTMLSelectElement>('journey-select');
      if (select) {
        select.value = created.id;
        localStorage.setItem('avgexpert_last_journey_id', created.id);
        await loadJourneyDetails(created.id);
      }
    } catch (err: any) {
      showToast(err.message || 'Ошибка создания истории', 'error');
    }
  });

  const select = $<HTMLSelectElement>('journey-select');
  select?.addEventListener('change', async () => {
    const val = select.value;
    if (!val) return;
    syncViewModeFromJourneyId(val);
    localStorage.setItem('avgexpert_last_journey_id', val);
    await loadJourneyDetails(val);
  });

  const deleteBtn = $('btn-delete-journey');
  deleteBtn?.addEventListener('click', async () => {
    if (!trajState.currentJourney) return;
    if (isJourneyReadOnly(trajState.currentJourney)) {
      showToast('Демонстрационную историю нельзя удалить', 'error');
      return;
    }
    if (!confirm(`Вы действительно хотите удалить историю "${trajState.currentJourney.name}"? Это удалит все сферы, наблюдения и шаги!`)) return;

    try {
      await apiFetch(`/api/journeys/${trajState.currentJourney.id}`, {
        method: 'DELETE'
      });
      showToast('История удалена');
      await loadJourneys();
    } catch (err: any) {
      showToast(err.message || 'Ошибка удаления истории', 'error');
    }
  });

  const editBtn = $('btn-edit-journey');
  editBtn?.addEventListener('click', async () => {
    if (!trajState.currentJourney) return;
    if (isJourneyReadOnly(trajState.currentJourney)) {
      showToast('Демонстрационную историю нельзя редактировать', 'error');
      return;
    }
    const name = prompt('Редактировать название истории:', trajState.currentJourney.name);
    if (!name) return;
    const description = prompt('Редактировать описание:', trajState.currentJourney.description || '') || '';

    try {
      await apiFetch(`/api/journeys/${trajState.currentJourney.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, description })
      });
      showToast('История обновлена');
      await loadJourneyDetails(trajState.currentJourney.id);
    } catch (err: any) {
      showToast(err.message || 'Ошибка обновления истории', 'error');
    }
  });

  // Drawer Close Button
  $('btn-close-drawer')?.addEventListener('click', closeObservationDrawer);
  $('observation-drawer-backdrop')?.addEventListener('click', closeObservationDrawer);

  initJourneySharing();

  // --- Переключение видов: Карта / Грибница механизмов ---
  const tabColumns = $('tab-columns-view');
  const tabMycelium = $('tab-mycelium-view');
  if (tabColumns && tabMycelium) {
    tabColumns.addEventListener('click', () => {
      if (trajState.currentViewTab === 'columns') return;
      setJourneyView('columns');
      if (trajState.d3Graph) trajState.d3Graph.pause();
    });

    tabMycelium.addEventListener('click', async () => {
      if (trajState.currentViewTab === 'mycelium') return;
      setJourneyView('mycelium');

      if (!trajState.currentJourney) return;

      if (trajState.d3Graph) {
        trajState.d3Graph.destroy();
        trajState.d3Graph = null;
      }
      trajState.d3Graph = new D3JourneyGraph(
        'journey-mycelium-container',
        'journey-tree-svg',
        handleGraphNodeClick,
        () => loadJourneyDetails(trajState.currentJourney!.id)
      );
      await trajState.d3Graph.load(trajState.currentJourney.id);
    });
  }
}

// Lower journey modules reload data through these late-bound actions.
trajActions.reloadJourneys = loadJourneys;
trajActions.reloadDetails = loadJourneyDetails;
